import { useEffect } from 'react';
import type { TenantBranding } from '../types/dtos';

/** Parse a #RRGGBB hex string into [r, g, b] integers, or null if invalid. */
function parseHex(hex: string): [number, number, number] | null {
  if (!/^#[0-9A-Fa-f]{6}$/.test(hex)) return null;
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

/** Darken an RGB triplet by `factor` (0–1). */
function darken([r, g, b]: [number, number, number], factor: number): [number, number, number] {
  return [
    Math.max(0, Math.round(r * (1 - factor))),
    Math.max(0, Math.round(g * (1 - factor))),
    Math.max(0, Math.round(b * (1 - factor))),
  ];
}

/** Lighten an RGB triplet by blending toward white by `factor` (0–1). */
function lighten([r, g, b]: [number, number, number], factor: number): [number, number, number] {
  return [
    Math.min(255, Math.round(r + (255 - r) * factor)),
    Math.min(255, Math.round(g + (255 - g) * factor)),
    Math.min(255, Math.round(b + (255 - b) * factor)),
  ];
}

const CSS_VARS = ['--brand-primary', '--brand-primary-hover', '--brand-primary-ring'] as const;

/**
 * Side-effect hook: injects per-tenant brand colors as CSS custom properties on <html>.
 * Falls back to default teal palette (defined in index.css) when branding is absent/null.
 * Removes inline overrides on unmount so the CSS-file fallback reasserts.
 */
export function useBrandTheme(branding: TenantBranding | null | undefined): void {
  useEffect(() => {
    const root = document.documentElement;
    const color = branding?.primaryColor ?? null;

    if (color) {
      const rgb = parseHex(color);
      if (rgb) {
        root.style.setProperty('--brand-primary', rgb.join(' '));
        root.style.setProperty('--brand-primary-hover', darken(rgb, 0.1).join(' '));
        root.style.setProperty('--brand-primary-ring', lighten(rgb, 0.6).join(' '));
        return () => {
          for (const v of CSS_VARS) root.style.removeProperty(v);
        };
      }
    }

    // No valid color — ensure any previous inline override is cleared so CSS fallback applies.
    for (const v of CSS_VARS) root.style.removeProperty(v);
  }, [branding?.primaryColor]);
}
